import {useState} from "react";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import AppModalBox from "./AppModalBox";
import CourseLogoPreview from "./CourseLogoPreview";
import {createCourseLogo} from "../api/api";

interface Props {
    onClose: () => void;
    onSuccess: () => void;
    onError: (error: string) => void;
}

const CourseLogoForm = ({
                            onClose,
                            onSuccess,
                            onError,
                        }: Props) => {
    const {t} = useTranslation();

    const [name, setName] = useState("");
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            onError(t("courselogoslist.errors.nameRequired"));
            return;
        }

        if (!content.trim()) {
            onError(t("courselogoslist.errors.contentRequired"));
            return;
        }

        try {
            setLoading(true);

            await createCourseLogo({
                name: name.trim(),
                content: content.trim(),
            });

            onSuccess();
        } catch (error: any) {
            onError(
                error?.message ||
                t("courselogoslist.errors.createFailed")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <AppModalBox
            sx={{
                margin: "auto",
                width: {
                    xs: "calc(100vw - 24px)",
                    sm: 650,
                },
                maxWidth: "95vw",
                maxHeight: "95vh",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: {
                    xs: 2,
                    sm: 3,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                }}
            >
                <Box
                    sx={(theme) => ({
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                    })}
                >
                    <ImageOutlinedIcon/>
                </Box>

                <Box>
                    <Typography
                        variant="h6"
                        sx={{fontWeight: 900}}
                    >
                        {t("courselogoslist.addLogo")}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {t("courselogoslist.addLogoDescription")}
                    </Typography>
                </Box>
            </Box>

            <form onSubmit={handleSubmit}>
                <TextField
                    label={t("courselogoslist.name")}
                    fullWidth
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                    inputProps={{
                        maxLength: 255,
                    }}
                    sx={{mb: 2}}
                />

                <TextField
                    label={t("courselogoslist.content")}
                    fullWidth
                    required
                    multiline
                    minRows={9}
                    maxRows={16}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    disabled={loading}
                    placeholder="<svg>...</svg>"
                    sx={{
                        mb: 2,
                        "& textarea": {
                            fontFamily: "monospace",
                            fontSize: 13,
                        },
                    }}
                />

                <Box
                    sx={(theme) => ({
                        mb: 2,
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.action.hover,
                    })}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 1.5,
                            fontWeight: 800,
                        }}
                    >
                        {t("courselogoslist.preview")}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <CourseLogoPreview
                            name={name}
                            content={content}
                            size={150}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        flexDirection: {
                            xs: "column-reverse",
                            sm: "row",
                        },
                    }}
                >
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        fullWidth
                        variant="outlined"
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                        }}
                    >
                        {t("common.cancel")}
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        fullWidth
                        sx={{
                            textTransform: "none",
                            fontWeight: 800,
                        }}
                    >
                        {loading
                            ? <CircularProgress size={22}/>
                            : t("common.create")
                        }
                    </Button>
                </Box>
            </form>
        </AppModalBox>
    );
};

export default CourseLogoForm;